/* ================================================================
   BlockFormatBar — enhanced formatting toolbar for contentEditable
   ================================================================
   Shows a Typography button (T) that opens the full TypographyPanel
   with font size, weight, color, bold, underline, lists, symbols.

   Supports two modes:
   - Full mode: when typo + onTypoChange provided (element-level styling)
   - Inline-only mode: always provides B/U/Lists/Symbols via the panel
   ================================================================ */

import { useState, useRef, useCallback } from "react";
import { Type } from "lucide-react";
import { TypographyPanel } from "./TypographyPanel";

export interface TypoData {
  fontSize?: string;
  color?: string;
  fontWeight?: string;
}

interface BlockFormatBarProps {
  editableEl: HTMLElement | null;
  /** Current typography values (optional) */
  typo?: TypoData;
  /** Callback to update typography (optional) */
  onTypoChange?: (changes: TypoData) => void;
  /** Callback to reset all typography (optional) */
  onTypoReset?: () => void;
  /** Show bullet/list buttons in the panel */
  showBullets?: boolean;
}

export function BlockFormatBar({
  editableEl,
  typo,
  onTypoChange,
  onTypoReset,
  showBullets = true,
}: BlockFormatBarProps) {
  const [showPanel, setShowPanel] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const fontSize = typo?.fontSize || "";
  const color = typo?.color || "";
  const fontWeight = typo?.fontWeight || "";
  const isTypoActive = !!(fontSize || color || fontWeight);

  const handleFontSize = useCallback((v: string) => {
    if (onTypoChange) {
      onTypoChange({ ...typo, fontSize: v || undefined });
    }
  }, [typo, onTypoChange]);

  const handleColor = useCallback((v: string) => {
    if (onTypoChange) {
      onTypoChange({ ...typo, color: v || undefined });
    }
  }, [typo, onTypoChange]);

  const handleWeight = useCallback((v: string) => {
    if (onTypoChange) {
      onTypoChange({ ...typo, fontWeight: v || undefined });
    }
  }, [typo, onTypoChange]);

  const handleReset = useCallback(() => {
    if (onTypoReset) {
      onTypoReset();
    } else if (onTypoChange) {
      onTypoChange({});
    }
  }, [onTypoReset, onTypoChange]);

  const handleClose = useCallback(() => {
    setShowPanel(false);
  }, []);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`btnReset w-6 h-6 flex items-center justify-center rounded-full shadow-sm cursor-pointer select-none ${
          showPanel
            ? "bg-purple-600 text-white ring-2 ring-purple-300"
            : isTypoActive
            ? "bg-purple-500 text-white hover:bg-purple-600"
            : "bg-gray-200 text-gray-600 hover:bg-purple-100 hover:text-purple-700"
        }`}
        title={"\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D8\u05E7\u05E1\u05D8 \u2014 \u05D2\u05D5\u05D3\u05DC, \u05E6\u05D1\u05E2, \u05E2\u05D5\u05D1\u05D9"}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowPanel((prev) => !prev);
        }}
      >
        <Type className="w-3 h-3" />
        {isTypoActive && !showPanel && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-400 rounded-full border border-white" />
        )}
      </button>

      {showPanel && (
        <TypographyPanel
          savedFontSize={fontSize}
          savedColor={color}
          savedFontWeight={fontWeight}
          onFontSize={handleFontSize}
          onColor={handleColor}
          onWeight={handleWeight}
          onReset={handleReset}
          onClose={handleClose}
          anchorEl={btnRef.current}
          editableEl={editableEl}
          showBullets={showBullets}
        />
      )}
    </>
  );
}
